// V2 Tempo — Personal Records aggregator. A pure-derived view over the
// existing MatchTelemetryRow stream. No new storage: every record below
// is computed on demand from the same localStorage rows that power the
// Profile panel and main-menu widget.
//
// Why "Personal Records" instead of an online leaderboard:
//   - The portal disallows server-side player identity (docs/13_PORTAL_CONSTRAINTS.md §8),
//     so a global leaderboard isn't available without a backend rework.
//   - Personal bests are a real, ethical engagement loop — chasing your
//     own fastest victory is just as motivating as chasing a stranger's,
//     and the player can't be discouraged by feeling out-classed by
//     strangers (a problem CrazyGamesTricks §6.7 specifically calls out
//     for browser games).
//   - These same numbers can later feed a server leaderboard with zero
//     UI rework — the API surface (PersonalRecords interface) is
//     leaderboard-shaped.

import type { MatchTelemetryRow } from './Telemetry.js';

export interface PersonalRecords {
  totalMatches: number;
  wins: number;
  losses: number;
  // Across ALL matches recorded. null if 0 matches.
  winRatePct: number | null;
  // Shortest match where the viewer (slot 0) won. null if no wins.
  fastestVictorySec: number | null;
  // Longest consecutive run of wins ending at the most recent match. null if
  // never won. The "current" streak — for showing "you're on a 3-win roll!"
  currentWinStreak: number;
  // Longest consecutive win streak ANYWHERE in history. null if never won.
  longestWinStreak: number;
  // Highest end-of-match signal score the viewer ever accumulated.
  peakSignalScore: number | null;
  // Highest end-of-match squads-built count.
  peakSquadsBuilt: number | null;
  // Highest end-of-match relay-flips count.
  peakRelayFlips: number | null;
  // Per-doctrine win rate. Only doctrines the viewer has actually played
  // appear here. Sorted by played count desc inside the renderer.
  doctrineMastery: Record<string, { played: number; wins: number; winRate: number }>;
  // Compact last-10 W/L vector for the sparkline. Index 0 = oldest of the
  // tail, last index = most recent.
  recentForm: ReadonlyArray<'W' | 'L'>;
}

const RECENT_FORM_LIMIT = 10;
const VIEWER_PLAYER_ID = 0;

// V2 Tempo hot-fix: Telemetry.isPlausibleRow only validates 4 fields
// (recordedAt / matchSeconds / doctrines / cachesPickedByPlayer), so rows
// written by older client versions can be missing any of the per-player
// arrays added later (signalScore, squadsBuilt, relayFlipsByPlayer, etc.)
// AND can have outcome === undefined (the legacy row format used a
// different shape). We defensively coerce every access so a stale
// localStorage payload never crashes the main menu boot.
function readArr2(arr: unknown, idx: 0 | 1): number {
  if (!Array.isArray(arr)) return 0;
  const v = arr[idx];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}
function viewerWon(row: MatchTelemetryRow): boolean {
  // `outcome` may be null or undefined on stale rows. We treat absence
  // as "not a viewer win" — neither inflates the streak/win-rate, nor
  // crashes.
  const outcome = (row as { outcome?: { winner?: unknown } }).outcome;
  return outcome !== undefined && outcome !== null && outcome.winner === VIEWER_PLAYER_ID;
}

export function computePersonalRecords(rows: readonly MatchTelemetryRow[]): PersonalRecords {
  const total = rows.length;
  let wins = 0;
  let losses = 0;
  let fastestVictorySec: number | null = null;
  let peakSignalScore: number | null = null;
  let peakSquadsBuilt: number | null = null;
  let peakRelayFlips: number | null = null;
  const doctrineMastery: PersonalRecords['doctrineMastery'] = {};

  for (const row of rows) {
    const won = viewerWon(row);
    if (won) wins += 1;
    else losses += 1;
    if (won) {
      if (fastestVictorySec === null || row.matchSeconds < fastestVictorySec) {
        fastestVictorySec = row.matchSeconds;
      }
    }
    const viewerSignal = readArr2(row.signalScore, VIEWER_PLAYER_ID);
    if (peakSignalScore === null || viewerSignal > peakSignalScore) {
      peakSignalScore = viewerSignal;
    }
    const viewerSquads = readArr2(row.squadsBuilt, VIEWER_PLAYER_ID);
    if (peakSquadsBuilt === null || viewerSquads > peakSquadsBuilt) {
      peakSquadsBuilt = viewerSquads;
    }
    const viewerFlips = readArr2(row.relayFlipsByPlayer, VIEWER_PLAYER_ID);
    if (peakRelayFlips === null || viewerFlips > peakRelayFlips) {
      peakRelayFlips = viewerFlips;
    }
    // `doctrines` is in the isPlausibleRow guard, so it's always an
    // array. The slot value may still be undefined on a stale row that
    // wrote a single-element array.
    const doctrine = Array.isArray(row.doctrines) ? row.doctrines[VIEWER_PLAYER_ID] ?? null : null;
    if (doctrine !== null) {
      const key = String(doctrine);
      const entry = doctrineMastery[key] ?? { played: 0, wins: 0, winRate: 0 };
      entry.played += 1;
      if (won) entry.wins += 1;
      entry.winRate = entry.played > 0 ? entry.wins / entry.played : 0;
      doctrineMastery[key] = entry;
    }
  }

  // Streaks: walk rows newest-first for the current streak (stops at the
  // first L), then walk forward for the lifetime maximum. Two passes are
  // cheaper than threading state through the main loop.
  let currentWinStreak = 0;
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i]!;
    if (!viewerWon(row)) break;
    currentWinStreak += 1;
  }
  let longestWinStreak = 0;
  let runningWinStreak = 0;
  for (const row of rows) {
    if (viewerWon(row)) {
      runningWinStreak += 1;
      if (runningWinStreak > longestWinStreak) longestWinStreak = runningWinStreak;
    } else {
      runningWinStreak = 0;
    }
  }

  const tail = rows.slice(-RECENT_FORM_LIMIT);
  const recentForm: Array<'W' | 'L'> = tail.map((row) =>
    viewerWon(row) ? 'W' : 'L',
  );

  return {
    totalMatches: total,
    wins,
    losses,
    winRatePct: total > 0 ? Math.round((wins / total) * 100) : null,
    fastestVictorySec,
    currentWinStreak,
    longestWinStreak,
    peakSignalScore,
    peakSquadsBuilt,
    peakRelayFlips,
    doctrineMastery,
    recentForm,
  };
}

// Formatting helpers reused by both the main-menu widget and the
// ProfilePanel so the records read identically in both surfaces.

export function formatVictoryTime(sec: number | null): string {
  if (sec === null) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function formatStreak(n: number): string {
  if (n <= 0) return '—';
  return `${n}W`;
}

export function formatScalar(n: number | null): string {
  if (n === null) return '—';
  return Math.round(n).toLocaleString();
}
